import { useState } from 'react';
import { Routes, Route, NavLink, useNavigate } from 'react-router-dom';
import { useTournamentStorage } from './hooks/useTournamentStorage';
import SetupPage from './pages/SetupPage';
import SchedulePage from './pages/SchedulePage';
import ScoreEntryPage from './pages/ScoreEntryPage';
import LeaderboardPage from './pages/LeaderboardPage';
import SummaryPage from './pages/SummaryPage';

function App() {
  const navigate = useNavigate();
  const { tournament, loading, saveTournament, updateTournament, resetTournament, exportTournament, importTournament } = useTournamentStorage();
  const [importError, setImportError] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleExport = () => {
    const data = exportTournament();
    if (data) {
      const blob = new Blob([data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tournament-${tournament?.config.name || 'export'}.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      const success = importTournament(content);
      if (success) {
        setImportError(null);
        navigate('/schedule');
      } else {
        setImportError('Invalid tournament file');
      }
    };
    reader.readAsText(file);
  };

  const handleReset = () => {
    if (confirm('Are you sure you want to reset? This will delete all tournament data.')) {
      resetTournament();
      navigate('/');
    }
  };

  if (loading) {
    return <div className="container">Loading...</div>;
  }

  return (
    <div>
      <header className="navbar">
        <h1>🎾 Pickleball Tournament</h1>
        
        {/* Mobile menu toggle */}
        <button 
          className="mobile-menu-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
          style={{
            display: 'none',
            background: 'transparent',
            border: 'none',
            color: 'white',
            fontSize: '1.5rem',
            cursor: 'pointer',
            padding: '0.5rem'
          }}
        >
          ☰
        </button>
        
        <nav className={mobileMenuOpen ? 'mobile-open' : ''}>
          <NavLink to="/" className={({ isActive }) => isActive ? 'active' : ''} onClick={() => setMobileMenuOpen(false)}>
            Setup
          </NavLink>
          {tournament && (
            <>
              <NavLink to="/schedule" className={({ isActive }) => isActive ? 'active' : ''} onClick={() => setMobileMenuOpen(false)}>
                Schedule
              </NavLink>
              <NavLink to="/scores" className={({ isActive }) => isActive ? 'active' : ''} onClick={() => setMobileMenuOpen(false)}>
                Scores
              </NavLink>
              <NavLink to="/leaderboard" className={({ isActive }) => isActive ? 'active' : ''} onClick={() => setMobileMenuOpen(false)}>
                Leaderboard
              </NavLink>
              <NavLink to="/summary" className={({ isActive }) => isActive ? 'active' : ''} onClick={() => setMobileMenuOpen(false)}>
                Summary
              </NavLink>
            </>
          )}
        </nav>
        <div className="flex gap-1 action-buttons">
          {tournament && (
            <>
              <button className="btn-secondary" onClick={handleExport} style={{ padding: '0.5rem 1rem' }}>
                Export
              </button>
              <button className="btn-danger" onClick={handleReset} style={{ padding: '0.5rem 1rem' }}>
                Reset
              </button>
            </>
          )}
          <label className="btn-primary" style={{ padding: '0.5rem 1rem', cursor: 'pointer' }}>
            Import
            <input type="file" accept=".json" onChange={handleImport} style={{ display: 'none' }} />
          </label>
        </div>
      </header>

      {importError && (
        <div className="container">
          <div className="error">{importError}</div>
        </div>
      )}

      <main className="container">
        <Routes>
          <Route
            path="/"
            element={
              <SetupPage
                tournament={tournament}
                onSave={saveTournament}
                onUpdate={updateTournament}
              />
            }
          />
          {tournament && (
            <>
              <Route
                path="/schedule"
                element={<SchedulePage tournament={tournament} />}
              />
              <Route
                path="/scores"
                element={
                  <ScoreEntryPage
                    tournament={tournament}
                    onUpdate={updateTournament}
                  />
                }
              />
              <Route
                path="/leaderboard"
                element={<LeaderboardPage tournament={tournament} />}
              />
              <Route
                path="/summary"
                element={<SummaryPage tournament={tournament} />}
              />
            </>
          )}
        </Routes>
      </main>
    </div>
  );
}

export default App;